import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { CompartimenteService } from '../compartimente.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { CompartimentAddPayload } from '../compartiment.interface';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { DirectiiService } from '../../directii/directii.service';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';

@Component({
    selector: 'app-add-compartiment',
    templateUrl: './add-compartiment.component.html',
    styleUrls: ['./add-compartiment.component.scss'],
})
export class AddCompartimentComponent implements OnInit, OnDestroy {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;
    form: FormGroup = new FormGroup({
        directie_id: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        name: new FormControl('', [Validators.required]),
        description: new FormControl(''),
    });

    directiiActive: Generic[] = [];

    private addDestroy$ = new Subject<void>();

    filteredDirectii: any[] = [];
    directiiControl = new FormControl();

    constructor(
        private dialogRef: MatDialogRef<AddCompartimentComponent>,
        private compartimenteService: CompartimenteService,
        private notificationService: NotificationService,
        public directiiService: DirectiiService,
        private authService: AuthService
    ) {
        this.processToken();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    ngOnInit() {
        this.loadDirectiiActive();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.directiiService
            .getDirectiiActive()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.directiiActive = res;
                    this.filteredDirectii = res;
                },
                error: (err) => {
                    console.error('Error in get directii active:', err);
                }
            });
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.compartimenteService.setToken(token);
        });
    }

    submitForm() {
        this.isLoading = true;

        const { directie_id, name, description } = this.form
            .value as CompartimentAddPayload;
        const slug = name
            .split(' ')
            .map((item) => item.toLowerCase())
            .join('_');
        const newCompartment: CompartimentAddPayload = {
            directie_id,
            name,
            description,
        };

        this.compartimenteService
            .addCompartiment(newCompartment)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
